import { AddressSuggestionDto, FeatureCollection, SaveFeatureCollection, ShapefileType } from '@agroone/entities'
import { getGeohashPrecision } from '@agroone/helpers'
import { inject, Injectable } from '@angular/core'
import { bbox, buffer, point } from '@turf/turf'
import { downloadBase64Zip } from '../../../utils/download.util'
import { requireDefined } from '../../../utils/require-defined.util'
import { bboxes } from 'ngeohash'
import { Observable, tap } from 'rxjs'
import { loadMapboxGeocoding } from '../utils/mapbox-loader.util'
import { GeolocationHttpService } from './geolocation.service'
import { MapTokenService } from './map-token.service'

@Injectable({ providedIn: 'root' })
export class MapGeoService {
  private readonly _tokenService = inject(MapTokenService)
  private readonly _geolocationHttpService = inject(GeolocationHttpService)

  async forwardGeocode(address: string) {
    const GeoService = await loadMapboxGeocoding()
    const token = await this._tokenService.ensureToken()
    const client = GeoService({ accessToken: token })
    const res = await client.forwardGeocode({ query: address, autocomplete: false, limit: 1 }).send()
    return res?.body?.features?.[0]
  }

  /**
   * Address autocompletion restricted to a country (5 results max).
   * The API picks the provider and normalizes the result, so suggestions have the same
   * shape everywhere and carry an INSEE code where the country has one.
   */
  public searchAddress(query: string, country: string): Observable<AddressSuggestionDto[]> {
    return this._geolocationHttpService.searchAddress(query, country)
  }

  async createGeohashes(
    location?: { latitude: number | string; longitude: number | string } | null,
    boundary?: GeoJSON.FeatureCollection | FeatureCollection | SaveFeatureCollection | null,
    address?: string | null,
  ): Promise<string[] | null> {
    let bBox: GeoJSON.BBox | null
    if (location) {
      const turfPoint = point([Number(location.longitude), Number(location.latitude)])
      const buffered = requireDefined(buffer(turfPoint, 500, { units: 'meters' }), 'the buffered location')
      bBox = bbox(buffered)
    } else if (boundary?.bbox) {
      bBox = boundary.bbox as GeoJSON.BBox
    } else if (address) {
      const feature = await this.forwardGeocode(address)
      bBox = feature?.bbox ?? null
    } else return null

    const box = requireDefined(bBox, 'the bounding box of the geocoded address')
    return bboxes(box[1], box[0], box[3], box[2], getGeohashPrecision(box))
  }

  /** Exports the boundaries as a shapefile (zip) and triggers the browser download. */
  public exportBoundariesShapefile(type: ShapefileType, ids: number[], boundaryIds?: number[]): Observable<string> {
    return this._geolocationHttpService.getShapefile(type, ids, boundaryIds).pipe(
      tap((data) => {
        if (data) {
          downloadBase64Zip(data, `Geolocation_export_${type}_${new Date().toLocaleString()}.zip`)
        }
      }),
    )
  }
}
